"use client";
import FrameLayout from "@/app/components/appcomponents/FrameLayout";
import ObituaryCard from "@/app/components/appcomponents/ObituaryCard";
import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import Link from "next/link";
import { useState, useEffect, useRef } from "react";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import obituaryService from "@/services/obituary-service";
import regionsAndCities from "@/utils/regionAndCities";
import { SelectDropdown } from "@/app/components/appcomponents/SelectDropdown";

// iFrame only: exclude Trzin (no obits for this city)
const regionsAndCitiesForIframe = Object.fromEntries(
  Object.entries(regionsAndCities).map(([region, cities]) => [
    region,
    cities.filter((c) => c !== "Trzin"),
  ])
);

/** Returns the region name that contains the given city, or null if not found. */
function getRegionForCity(cityName, regionsMap) {
  for (const [region, cities] of Object.entries(regionsMap)) {
    if (cities.includes(cityName)) return region;
  }
  return null;
}

const quickSelectCities = {
  1: [
    { name: "Pomurska", isRegion: true },
    { name: "Podravska", isRegion: true },
    { name: "Ljutomer", isRegion: false },
    { name: "Ormož", isRegion: false },
    { name: "Gornja Radgona", isRegion: false },
    { name: "Murska Sobota", isRegion: false },
    { name: "Ptuj", isRegion: false },
    { name: "Maribor", isRegion: false },
  ],
  2: [
    { name: "Ljubljana", isRegion: false },
    { name: "Maribor", isRegion: false },
    { name: "Celje", isRegion: false },
    { name: "Kranj", isRegion: false },
    { name: "Koper", isRegion: false },
    { name: "Murska Sobota", isRegion: false },
    { name: "Novo Mesto", isRegion: false },
    { name: "Ptuj", isRegion: false },
    { name: "Slovenj Gradec", isRegion: false },
    { name: "Trbovlje", isRegion: false },
    { name: "Velenje", isRegion: false },
  ],
  D3: [
    { name: "Domžale", isRegion: false },
    { name: "Kamnik", isRegion: false },
    { name: "Komenda", isRegion: false },
    { name: "Lukovica", isRegion: false },
    { name: "Medvode", isRegion: false },
    { name: "Mengeš", isRegion: false },
    { name: "Moravče", isRegion: false },
    { name: "Dol pri Ljubljani", isRegion: false },
  ],
  4: [
    { name: "Podravska", isRegion: true },
    { name: "Maribor", isRegion: false },
    { name: "Pesnica", isRegion: false },
    { name: "Šentilj", isRegion: false },
    { name: "Ruše", isRegion: false },
    { name: "Hoče - Slivnica", isRegion: false },
    { name: "Duplek", isRegion: false },
    { name: "Miklavž na Dravskem polju", isRegion: false },
  ],
};

export default function FramedContentComp() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const regionParam1 = searchParams.get("region");
  const cityParam1 = searchParams.get("city");
  const searchParam1 = searchParams.get("search");
  const partnerParam1 = searchParams.get("partner");
  const [obituaries, setObituaries] = useState([]);
  const [selectedCity, setSelectedCity] = useState(cityParam1 || null);
  const [selectedRegion, setSelectedRegion] = useState(regionParam1 ?? null);
  const [name, setName] = useState(() => {
    const s = searchParam1;
    if (s == null || String(s).trim() === "") return null;
    try {
      return decodeURIComponent(String(s)).replace(/\+/g, " ").trim() || null;
    } catch {
      return String(s).replace(/\+/g, " ").trim() || null;
    }
  });
  const [partner, setPartner] = useState(partnerParam1 || "default");
  const fetchIdRef = useRef(0);
  const urlAppliedOnceRef = useRef(false);
  const lastUrlReplaceByUsRef = useRef(0);

  const normalizeSearchTerm = (term) => {
    if (term == null || String(term).trim() === "") return "";
    try {
      return decodeURIComponent(String(term)).replace(/\+/g, " ").trim();
    } catch {
      return String(term).replace(/\+/g, " ").trim();
    }
  };

  // Option A: ?layout=2col only → 2 columns from 1020px; no param or other → 2 columns from 1280px
  const layoutParam = searchParams.get("layout");
  const use2colLayout = layoutParam === "2col";

  useEffect(() => {
    if (urlAppliedOnceRef.current) return;
    urlAppliedOnceRef.current = true;
    const cityParam = searchParams.get("city");
    const regionParam = searchParams.get("region");
    const partnerParam = searchParams.get("partner");
    const searchParam = searchParams.get("search");
    setPartner(partnerParam || "default");
    setSelectedCity(cityParam || null);
    setSelectedRegion(regionParam ?? null);
    const normalized = normalizeSearchTerm(searchParam);
    setName(normalized ? normalized : null);

      // Partner 1 iframe: default region Pomurska, city Ljutomer when none in URL
      if (partnerParam === "1" && (regionParam == null && cityParam == null)) {
        setSelectedRegion(regionParam ?? "Pomurska");
        setSelectedCity(cityParam ?? "Ljutomer");
        const params = new URLSearchParams(window.location.search);
        params.set("partner", "1");
        params.set("region", regionParam ?? "Pomurska");
        params.set("city", cityParam ?? "Ljutomer");
        if (searchParam) params.set("search", searchParam);
        router.replace(`?${params.toString()}`, { scroll: false });
      }

      // Partner 4 iframe: default region Podravska (opening all cities in region)
      if (partnerParam === "4" && regionParam == null && cityParam == null) {
        setSelectedRegion("Podravska");
        setSelectedCity(null);
        const params = new URLSearchParams(window.location.search);
        params.set("partner", "4");
        params.set("region", "Podravska");
        if (searchParam) params.set("search", searchParam);
        router.replace(`?${params.toString()}`, { scroll: false });
      }
    }, [searchParams, router]);

  const regionOptions = [
    { place: "- Pokaži vse regije -", id: "allRegions" },
    ...Object.keys(regionsAndCitiesForIframe).map((region) => ({
      place: region,
      id: region,
    })),
  ];
  regionOptions.map((item) => {
  });

  const allCitiesOption = { place: "- Pokaži vse občine -", id: "allCities" };
  const cityOptions =
    (
      selectedRegion &&
      selectedRegion !== "allRegions" &&
      selectedRegion !== "- Pokaži vse regije -"
    ) ?
      [
        allCitiesOption,
        ...(regionsAndCitiesForIframe[selectedRegion] || []).map((city) => ({
          place: city,
          id: city,
        })),
      ]
      : [
        allCitiesOption,
        ...Object.values(regionsAndCitiesForIframe)
          .flat()
          .map((city) => ({
            place: city,
            id: city,
          }))
          .sort((a, b) => a.place.localeCompare(b.place, "sl")),
      ];

  const handleRegionSelect = (item) => {
    if (item.id === "allRegions" || item.id === "- Pokaži vse regije -") {
      setSelectedRegion(null);
      setSelectedCity(null);
      updateURL(null, null, name);
    } else {
      setSelectedRegion(item.place);
      setSelectedCity(null);
      updateURL("", item.place, name);
    }
  };

  const handleNameChange = (name) => {
    setName(name);
    updateURL(selectedCity, selectedRegion, name);
  };

  const updateURL = (city, region, search) => {
    const params = new URLSearchParams(window.location.search);
    if (city && city !== "allCities" && city !== "- Pokaži vse občine -") {
      params.set("city", city);
    } else {
      params.delete("city");
    }
    if (
      region &&
      region !== "allRegions" &&
      region !== "- Pokaži vse regije -"
    ) {
      params.set("region", region);
    } else {
      params.delete("region");
    }
    if (search) {
      params.set("search", search);
    } else {
      params.delete("search");
    }
    const queryString = params.toString();
    const newURL = queryString ? `?${queryString}` : window.location.pathname;
    lastUrlReplaceByUsRef.current = Date.now();
    router.replace(newURL, { scroll: false });
  };

  const handleCitySelect = (item) => {
    if (item.id === "allCities" || item.place === "- Pokaži vse občine -") {
      setSelectedCity(null);
      updateURL(null, selectedRegion, name);
    } else {
      setSelectedCity(item.place);
      const cityRegion = getRegionForCity(item.place, regionsAndCitiesForIframe);
      const regionToUse =
        selectedRegion && cityRegion === selectedRegion ? selectedRegion : null;
      if (regionToUse !== selectedRegion) {
        setSelectedRegion(null);
      }
      updateURL(item.place, regionToUse, name);
    }
  };

  const prevNameRef = useRef(name);
  const searchDebounceRef = useRef(null);
  useEffect(() => {
    const nameChanged = prevNameRef.current !== name;
    prevNameRef.current = name;
    if (nameChanged) {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = setTimeout(() => {
        searchDebounceRef.current = null;
        fetchObituary();
      }, 650);
      return () => {
        if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
      };
    }
    fetchObituary();
  }, [selectedCity, selectedRegion, name]);

  const fetchObituary = async () => {
    const currentFetchId = ++fetchIdRef.current;
    try {
      const queryParams = { limit: 8, page: 1 };

      if (selectedCity && selectedCity != "- Pokaži vse občine -")
        queryParams.city = selectedCity;
      if (selectedRegion && selectedRegion != "- Pokaži vse regije -")
        queryParams.region = selectedRegion;
      if (name != null && String(name).trim() !== "")
        queryParams.name = String(name).trim();

      const response = await obituaryService.getObituary(queryParams);

      if (currentFetchId !== fetchIdRef.current) return;

      if (!response || response.error) {
        setObituaries([]);
        return;
      }
      setObituaries(response.obituaries || []);
    } catch (err) {
      if (currentFetchId !== fetchIdRef.current) return;
      console.error("Error fetching obituaries:", err);
      setObituaries([]);
    }
  };

  const updateObituary = (id) => {
    setObituaries((prev) =>
      prev.map((item) =>
        item.id === id
          ? { ...item, totalCandles: (item.totalCandles ?? 0) + 1 }
          : item
      )
    );
  };

  const funcMegaMenu = () => { };
  const handleQuickSelect = (item) => {
    if (item.isRegion) {
      handleRegionSelect({ place: item.name, id: item.name });
    } else {
      handleCitySelect({ place: item.name, id: item.name });
    }
  };

  const osmrtniceHref = (() => {
    const params = new URLSearchParams();
    if (selectedCity && selectedCity !== "allCities" && selectedCity !== "- Pokaži vse občine -") params.set("city", selectedCity);
    if (selectedRegion && selectedRegion !== "allRegions" && selectedRegion !== "- Pokaži vse regije -") params.set("region", selectedRegion);
    const q = params.toString();
    return `/osmrtnice${q ? `?${q}` : ""}`;
  })();

  return (
    <FrameLayout
      isMegaMenuVisible={false}
      from={"1"}
      forFooter={""}
      megaMenu={funcMegaMenu}
    >
      <div
        className={`flex flex-col items-center tablet:w-[680px] mobile:w-[100%] mobile:max-w-[500px] mx-auto mobile:px-3 ${use2colLayout ? "desktopEmbed:w-[1020px]" : "desktop:w-[1280px]"}`}
      >
        <div
          className="flex items-center 
        desktopEmbed:mt-[60.73px] desktop:mt-[60.73px] tablet:mt-[67.73px] mobile:mt-[22px]
        desktopEmbed:h-[47px] desktop:h-[47px] tablet:h-[47px] mobile:h-[33px]"
        >
          <Link
            href={"/moj-racun"}
            className="mobile:font-variation-customOpt28 tablet:font-variation-customOpt40 desktopEmbed:font-variation-customOpt40 desktop:font-variation-customOpt40 desktopEmbed:text-[40px] desktop:text-[40px] tablet:text-[40px] mobile:text-[28px] text-[#1E2125] leading-[46.88px]"
          >
            Zadnje osmrtnice
          </Link>
        </div>
        <div
          className="flex flex-col desktopEmbed:flex-row desktop:flex-row flex-nowrap
        desktopEmbed:mt-[70px] desktop:mt-[70px] tablet:mt-[48px] mobile:mt-[29px]
        desktopEmbed:w-[777px] desktop:w-[777px] tablet:w-[600px] mobile:w-[296px]
        desktopEmbed:min-w-[777px] desktop:min-w-[777px]
          items-center justify-center desktopEmbed:space-x-[16px] desktop:space-x-[16px]"
        >
          <div className="flex flex-col w-full items-center tablet:flex-row desktopEmbed:flex-row desktop:flex-row desktopEmbed:flex-nowrap desktop:flex-nowrap desktopEmbed:space-x-[16px] desktop:space-x-[16px] tablet:justify-between mobile:h-[112px] tablet:h-[48px] desktopEmbed:h-[48px] desktop:h-[48px]">
            <div className="hidden desktopEmbed:flex desktop:flex h-[48px] shrink-0">
              <input
                type="text"
                placeholder="Išči po imenu / priimku"
                value={name ?? ""}
                style={{
                  fontSize: "16px",
                  lineHeight: "24px",
                  width: "227px",
                  height: "100%",
                  fontWeight: 400,
                  borderWidth: "1px",
                  borderColor: "#7C7C7C",
                  borderRadius: "8px",
                  paddingLeft: "15.8px",
                  paddingRight: "15.8px",
                  color: "#7C7C7C",
                  backgroundColor: "white",
                  fontVariationSettings: "'opsz' 16",
                }}
                onChange={(e) => handleNameChange(e.target.value)}
              />
            </div>
            <SelectDropdown
              label={"Išči po regiji"}
              isFromNotification={false}
              isFromFlower={false}
              isFromEmbed={true}
              data={regionOptions}
              selectedValue={selectedRegion}
              onSelect={handleRegionSelect}
            />
            <div className="flex h-[16px] w-[360px] tablet:hidden desktopEmbed:hidden desktop:hidden" />
            <SelectDropdown
              data={cityOptions}
              label={"Išči po kraju"}
              isFromNotification={false}
              isFromFlower={false}
              isFromEmbed={true}
              selectedValue={selectedCity}
              onSelect={handleCitySelect}
            />
          </div>
          <div
            onClick={() => fetchObituary()}
            className="hidden desktopEmbed:flex desktop:flex justify-center w-12 shrink-0 items-center h-full desktopEmbed:aspect-square desktop:aspect-square rounded-lg bg-[#414141]"
          >
            <MagnifyingGlassIcon className="w-5 h-5 text-white hidden desktopEmbed:block desktop:block" />
          </div>
        </div>

        <div className="flex desktopEmbed:hidden desktop:hidden h-[48px] mt-4 w-[296px] tablet:w-[292px] bg-[#414141] rounded-[8px] justify-center items-center ">
          <div
            style={{
              fontSize: "16px",
              lineHeight: "24px",
              fontWeight: 400,
              color: "#F6F6F6",
              fontVariationSettings: "'opsz' 16",
            }}
          >
            Prikaži
          </div>
        </div>

        {(() => {
          switch (partner) {
            case "1":
              // Partner 1 specific logic
              return (
                <div className="flex flex-col desktopEmbed:flex-col desktop:flex-col tablet:flex-col mobile:flex-col desktopEmbed:items-start desktop:items-start items-start desktopEmbed:justify-center desktop:justify-center tablet:justify-center desktopEmbed:mt-[48px] desktop:mt-[48px] tablet:mt-[48px] mobile:mt-[32px] desktopEmbed:mb-[48px] desktop:mb-[48px] tablet:mb-[48px] mobile:mb-[32px]">
                  <h2 className="flex text-[32px] mobile:text-[24px] tablet:text-[24px] font-[400px] leading-[28.13px] text-[#1E2125] whitespace-nowrap mobile:h-7 tablet:h-7 desktopEmbed:h-auto desktop:h-auto mobile:mr-[24px] tablet:mr-[18px] desktopEmbed:mr-0 desktop:mr-0 desktopEmbed:mb-4 desktop:mb-4 mobile:w-full mobile:text-left">
                    Hitri izbor
                  </h2>
                  <div className="flex w-full mobile:max-w-[310px] tablet:max-w-none desktopEmbed:max-w-none desktop:max-w-none mt-3 desktopEmbed:mt-3 desktop:mt-3">
                    <ul className="flex flex-row list-none flex-wrap gap-[15px] w-full min-w-0 mobile:ml-[0px] desktopEmbed:justify-center desktop:justify-center">
                      {(quickSelectCities[partner] ?? []).map((item) => (
                        <li key={item.name} className="flex items-center shrink-0">
                          <button
                            onClick={() => {
                              handleQuickSelect(item);
                            }}
                            className={`whitespace-nowrap border border-[#C3C6C8] rounded-sm text-[#3C3E41] hover:bg-gray-100 transition-colors cursor-pointer ${(
                              selectedCity === item.name ||
                              selectedCity?.name === item.name
                            ) ?
                              "bg-[#414141] text-white"
                              : "bg-gradient-to-br from-[#E3E8EC] to-[#FFFFFF]"
                              } text-[14px] mobile:text-[13px] font-extrabold tablet:font-bold mobile:font-bold italic leading-[16.41px] mobile:px-[6px] px-[7.5px] py-[4px]`}
                          >
                            {item.name}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            case "4":
            case "D3":
              return (
                <div className="flex flex-col desktopEmbed:flex-col desktop:flex-col tablet:flex-col mobile:flex-col desktopEmbed:items-start desktop:items-start items-start desktopEmbed:justify-center desktop:justify-center tablet:justify-center desktopEmbed:mt-[48px] desktop:mt-[48px] tablet:mt-[48px] mobile:mt-[32px] desktopEmbed:mb-[48px] desktop:mb-[48px] tablet:mb-[48px] mobile:mb-[32px]">
                  <h2 className="flex text-[32px] mobile:text-[24px] tablet:text-[24px] font-[400px] leading-[28.13px] text-[#1E2125] whitespace-nowrap mobile:h-7 tablet:h-7 desktopEmbed:h-auto desktop:h-auto mobile:mr-[24px] tablet:mr-[18px] desktopEmbed:mr-0 desktop:mr-0 desktopEmbed:mb-4 desktop:mb-4 mobile:w-full mobile:text-left">
                    Hitri izbor
                  </h2>
                  <div className="flex w-full mobile:max-w-[310px] tablet:max-w-none desktopEmbed:max-w-none desktop:max-w-none mt-3 desktopEmbed:mt-3 desktop:mt-3">
                    <ul className="flex flex-row list-none flex-wrap gap-[15px] w-full min-w-0 mobile:ml-[0px] desktopEmbed:justify-center desktop:justify-center">
                      {(quickSelectCities[partner] ?? []).map((item) => (
                        <li key={item.name} className="flex items-center shrink-0">
                          <button
                            onClick={() => {
                              handleQuickSelect(item);
                            }}
                            className={`whitespace-nowrap border border-[#C3C6C8] rounded-sm text-[#3C3E41] hover:bg-gray-100 transition-colors cursor-pointer ${(
                              selectedCity === item.name ||
                              selectedCity?.name === item.name ||
                              (item.isRegion && selectedRegion === item.name && !selectedCity)
                            ) ?
                              "bg-[#414141] text-white"
                              : "bg-gradient-to-br from-[#E3E8EC] to-[#FFFFFF]"
                              } text-[14px] mobile:text-[13px] font-extrabold tablet:font-bold mobile:font-bold italic leading-[16.41px] mobile:px-[6px] px-[7.5px] py-[4px]`}
                          >
                            {item.name}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            case "partner2":
              // Partner 2 specific logic
              return (
                <div className="text-black text-lg">
                  Special menu for Partner 2{" "}
                </div>
              );
            default:
              return null;
          }
        })()}

        <div
          className={
            use2colLayout
              ? "mx-auto hidden tablet:hidden desktopEmbed:grid desktopEmbed:grid-cols-2 grid-cols-1 tablet:gap-6 desktopEmbed:gap-6 mt-[43px] tablet:mt-[64px] desktopEmbed:mt-[47.93px] justify-between"
              : "mx-auto hidden tablet:hidden desktop:grid desktop:grid-cols-2 grid-cols-1 tablet:gap-6 desktop:gap-6 mt-[43px] tablet:mt-[64px] desktop:mt-[47.93px] justify-between"
          }
        >
          {obituaries.slice(0, 8).map((obituary, index) => (
            <ObituaryCard
              data={obituary}
              index={index}
              key={obituary.id}
              mob={false}
              selectedCity={selectedCity}
              selectedRegion={selectedRegion}
              iframed={true}
              onCandleBurned={updateObituary}
            />
          ))}
        </div>
        <div
          className={
            use2colLayout
              ? "mx-auto hidden tablet:grid grid-cols-1 desktopEmbed:hidden tablet:gap-6 desktopEmbed:gap-6 mt-[43px] tablet:mt-[64px] desktopEmbed:mt-[47.93px] justify-between"
              : "mx-auto hidden tablet:grid grid-cols-1 desktop:hidden tablet:gap-6 desktop:gap-6 mt-[43px] tablet:mt-[64px] desktop:mt-[47.93px] justify-between"
          }
        >
          {obituaries.slice(0, 8).map((obituary, index) => (
            <ObituaryCard
              data={obituary}
              index={index}
              key={obituary.id}
              mob={false}
              selectedCity={selectedCity}
              selectedRegion={selectedRegion}
              iframed={true}
              onCandleBurned={updateObituary}
            />
          ))}
        </div>
        <div
          className={`mx-auto mobile:w-full grid grid-cols-1 tablet:hidden gap-[22px] tablet:gap-6 mt-[43px] tablet:mt-[64px] mobile:place-items-center ${use2colLayout ? "desktopEmbed:hidden" : "desktop:hidden"}`}
        >
          {obituaries.slice(0, 8).map((obituary, index) => (
            <ObituaryCard
              data={obituary}
              index={index}
              key={obituary.id}
              mob={true}
              selectedCity={selectedCity}
              selectedRegion={selectedRegion}
              iframed={true}
              onCandleBurned={updateObituary}
            />
          ))}
        </div>

        <div
          className="flex flex-col items-center justify-center 
        mobile:pt-[22.09px] mobile:pb-[49px]
        tablet:pt-[43px] tablet:pb-[46px]
        desktop:pt-[45px] desktop:pb-[62px]  "
        >
          <Link
            href={osmrtniceHref}
            target="_blank"
            className="flex items-center rounded-lg justify-center shadow-custom-light-dark bg-gradient-to-br from-[#E3E8EC] to-[#FFFFFF]
          mobile:h-[43px] mobile:w-[103px]
          tablet:h-[43px] tablet:w-[97px]
          desktop:h-12 desktop:w-[125px]"
          >
            <div className="flex font-variation-customOpt16 text-[16px] text-[#1E2125] text-center">
              Naprej
            </div>
          </Link>

          <div>
            <Link
              href={osmrtniceHref}
              target="_blank"
              className="text-center mt-4 text-[14px] text-[#1E2125] flex items-center justify-center"
            >
              odpre stran
              <img src={"/omr.png"} className="ml-2 w-[105px]" />
            </Link>
          </div>
        </div>
      </div>
    </FrameLayout>
  );
}
